import React, {useMemo} from 'react';
import {Box, styled} from '@mui/material';
import translate from '@/utils/translate';
import {HealthEvaluation} from '@/types/healthEvaluation';
import {Post} from '@/types/post';
import Conditional from '@/components/conditional';
import MetricCard from './MetricCard';
import MetricCardAverageComparison from './MetricCardAverageComparison';
import Rating from './Rating';

interface SocialScoresProps {
  testID?: string;
  healthEvaluation: HealthEvaluation;
  followersCount: number;
  posts: Post[];
  cachedPostFrequency60D: number;
}

function hasViewCount(post: Post): post is Post & {viewCount: number} {
  return post.viewCount != null;
}

function getMedian(posts: Post[]) {
  // TODO: add reels to this calculation, right now it's only feed because
  // API doesn't return reels posts(needs to be addressed in the future)
  const feedPosts = posts.filter((post) => post.socialStream === 'feed');

  const postsWithViewCount = feedPosts.filter(hasViewCount);

  const mostRecentPosts = postsWithViewCount
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    .slice(0, 30);

  if (mostRecentPosts.length > 0) {
    const sortedPosts = mostRecentPosts.sort((a, b) => {
      return a.viewCount - b.viewCount;
    });
    const middle = Math.floor(sortedPosts.length / 2);
    return sortedPosts[middle].viewCount;
  }
  return null;
}

function formatMedian(median: number): string {
  return median > 1000
    ? `${(median / 1000).toFixed(2)}${translate('K')}`
    : `${median}`;
}

function formatFrequency(frequency: number): string {
  if (frequency === 0) {
    return translate('Inactive (no posts in last 60 days)');
  }

  const weeksIn60Days = 60 / 7;
  const postsPerWeek = frequency / weeksIn60Days;

  if (postsPerWeek >= 1) {
    return `${Math.round(postsPerWeek)}${translate('/week')}`;
  }

  const monthsIn60Days = 2;
  const postsPerMonth = frequency / monthsIn60Days;

  if (postsPerMonth >= 1) {
    return `${Math.round(postsPerMonth)}${translate('/month')}`;
  }

  return translate('(less than 1/month)');
}

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MetricsRow = styled(Box)`
  display: flex;
  justify-content: space-between;
`;

const MetricCardContainer = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetricCardContainerWithBorder = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-left: 1px solid var(--divider, rgba(44, 45, 48, 0.12));
  margin-left: 24px;
  padding-left: 24px;
`;

function HealthScores(props: SocialScoresProps) {
  const {
    testID = 'health-scores',
    healthEvaluation,
    followersCount,
    posts,
    cachedPostFrequency60D,
  } = props;

  const posts_feed_median = useMemo(() => getMedian(posts), [posts]);

  return (
    <Container data-testid={testID}>
      <Conditional condition={healthEvaluation.rating === 'healthy'}>
        <Rating healthEvaluation={healthEvaluation} />
      </Conditional>
      <MetricsRow>
        <MetricCardContainer>
          <MetricCard
            title={translate('Followers')}
            value={`${(followersCount / 1000).toFixed(2)}K`}
          />
        </MetricCardContainer>
        <MetricCardContainerWithBorder>
          <MetricCard
            title={translate('Eng Rate')}
            value={`${((healthEvaluation.engagementRate || 0) * 100).toFixed(
              2
            )}%`}
          />
          <MetricCardAverageComparison
            value={healthEvaluation.engagementRate || 0}
            average={
              healthEvaluation.dataSets.find(
                (dataSet) => dataSet.statistic === 'engagement_rate'
              )?.mean ?? 0
            }
          />
        </MetricCardContainerWithBorder>
        <MetricCardContainerWithBorder>
          <MetricCard
            title={translate('Average views')}
            value={formatMedian(posts_feed_median ?? 0)}
          />
        </MetricCardContainerWithBorder>
        <MetricCardContainerWithBorder>
          <MetricCard
            title={translate('Posting Frequency')}
            value={formatFrequency(cachedPostFrequency60D)}
            variant={cachedPostFrequency60D === 0 ? 'small' : 'default'}
          />
        </MetricCardContainerWithBorder>
      </MetricsRow>
    </Container>
  );
}

export default HealthScores;
